import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { poszerzKolumne, zwezKolumne, type Propozycja } from '@/lib/planogram-api';
import { useI18n } from '@/lib/i18n';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  EDYCJA SZEROKOŚCI KOLUMN — PASEK POTWIERDZENIA POD SIATKĄ.
 *
 *  ⚠️⚠️ ZNAKI ZŁĄCZENIA SIEDZĄ NA ZWYKŁEJ SIATCE PLANOGRAMU, a nie na jej kopii — poprawione po
 *  obejrzeniu ekranu (22.09.2026, użytkownik): „chcę żeby tylko na bieżącym widoku doszły znaczki
 *  złączania. Dzięki temu widzimy od razu jaki towar jest i nie dublujemy ekranów". Druga siatka
 *  pokazywała puste prostokąty i kazała pamiętać, co w nich stało.
 *
 *  ⚠️ TU ZOSTAJE WYŁĄCZNIE POTWIERDZENIE. Ostrzeżenie jest już na siatce — kolumna do wchłonięcia
 *  jest obwiedziona na czerwono razem z towarem, który w niej leży. Ten pasek dokłada do obrazu
 *  liczby i pyta.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export type WybranaZmiana = { co: 'poszerz' | 'zwez'; p: Propozycja; picNumber: number };

export function PasekEdycjiKolumn({
  groupId,
  vmid,
  poziomow,
  wybrana,
  onZamknij,
}: {
  groupId: string;
  vmid: number;
  poziomow: number;
  wybrana: WybranaZmiana;
  onZamknij: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();

  const wykonaj = useMutation({
    mutationFn: async () => {
      const p = {
        groupId,
        vmid,
        picNumber: wybrana.picNumber,
        /** ⚠️ Poszerzenie adresuje kolumnę WCHŁANIANĄ, zwężenie — tę, która oddaje. */
        kolumna: wybrana.co === 'poszerz' ? wybrana.p.kolumna : wybrana.p.zostaje,
      };
      return wybrana.co === 'poszerz' ? poszerzKolumne(p) : zwezKolumne(p);
    },
    onSuccess: async () => {
      toast.success(t('szer.zakolejkowano'));
      onZamknij();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['kolumny-bebna'] }),
        qc.invalidateQueries({ queryKey: ['planogram'] }),
      ]);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });

  return (
    <div className="mt-3 space-y-2 rounded border border-border bg-muted/20 p-3">
      {wybrana.co === 'poszerz' ? (
        <div className="flex items-start gap-2">
          {/* ⚠️ Znak ostrzegawczy TYLKO przy utracie towaru — przy pustej kolumnie byłby szumem. */}
          {wybrana.p.poziom === 'GINIE_TOWAR' && (
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--destructive)]" />
          )}
          <div className="space-y-1">
            <p className="text-sm">
              {t(`szer.skutek.${wybrana.p.poziom}`)
                .replace('{zostaje}', String(wybrana.p.zostaje))
                .replace('{kolumna}', String(wybrana.p.kolumna))
                .replace('{przegrod}', String(wybrana.p.skutek.przegrodek))
                .replace('{pozycji}', String(wybrana.p.skutek.pozycji))
                .replace('{sztuk}', String(wybrana.p.skutek.sztuk))}
            </p>
            {/*
              ⚠️⚠️ TYLKO GDY JEST CO STRACIĆ, i tylko o TOWARZE. Wcześniej stało tu „nie da się tego
              cofnąć" przy każdym poszerzeniu — nieprawda, obalona przez użytkownika 22.09.2026:
              „rozdzielenie wiersza to jest cofnięcie tej operacji". Złączenie cofa zwężenie; nie
              wraca wyłącznie towar. Przy pustej kolumnie nie ma więc o czym mówić.
            */}
            {wybrana.p.poziom !== 'BEZ_SKUTKU' && (
              <p className="text-xs text-muted-foreground">{t('szer.towarNieWroci')}</p>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm">
          {t('szer.skutekZwezenia')
            .replace('{zostaje}', String(wybrana.p.zostaje))
            .replace('{kolumna}', String(wybrana.p.kolumna))
            .replace('{przegrod}', String(poziomow))}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={wybrana.co === 'poszerz' ? 'destructive' : 'outline'}
          disabled={wykonaj.isPending}
          onClick={() => wykonaj.mutate()}
        >
          {wykonaj.isPending && <Loader2 className="size-4 animate-spin" />}
          {wybrana.co === 'poszerz'
            ? t('szer.potwierdzPoszerzenie').replace('{n}', String(wybrana.p.zostaje))
            : t('szer.potwierdzZwezenie').replace('{n}', String(wybrana.p.zostaje))}
        </Button>
        <Button size="sm" variant="outline" disabled={wykonaj.isPending} onClick={onZamknij}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}
